import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase, UserProfile } from '../lib/supabase'
import { User, Session } from '@supabase/supabase-js'

interface AuthContextType {
  user: User | null
  profile: UserProfile | null
  session: Session | null
  loading: boolean
  signUp: (email: string, password: string, username: string) => Promise<{ error: Error | null }>
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: Error | null }>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

// Demo user for when Supabase is not configured
const DEMO_USER: User = {
  id: 'demo-user-123',
  email: 'demo@overlap.game',
  user_metadata: {},
  app_metadata: {},
  aud: 'authenticated',
  created_at: new Date().toISOString(),
} as User

const DEMO_PROFILE: UserProfile = {
  id: 'demo-user-123',
  username: 'demo_player',
  display_name: 'Demo Player',
  avatar_url: null,
  current_streak: 7,
  longest_streak: 14,
  total_plays: 42,
  freezes_earned: 2,
  freezes_used: 0,
  created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  updated_at: new Date().toISOString(),
}

const DEMO_SESSION: Session = {
  access_token: 'demo-token',
  refresh_token: 'demo-refresh',
  expires_in: 3600,
  expires_at: Date.now() + 3600000,
  token_type: 'bearer',
  user: DEMO_USER,
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    const initAuth = async () => {
      if (!supabase) {
        // Demo mode - no Supabase configured
        setUser(DEMO_USER)
        setProfile(DEMO_PROFILE)
        setSession(DEMO_SESSION)
        setLoading(false)
        return
      }
      
      const { data: { session } } = await supabase.auth.getSession()
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        await fetchProfile(session.user.id)
      } else {
        // No session but Supabase is configured - use demo mode
        setUser(DEMO_USER)
        setProfile(DEMO_PROFILE)
        setSession(DEMO_SESSION)
        setLoading(false)
      }
    }
    
    initAuth()

    // Listen for auth changes
    if (supabase) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        
        if (session?.user) {
          await fetchProfile(session.user.id)
        } else {
          setProfile(null)
          setLoading(false)
        }
      })

      return () => subscription.unsubscribe()
    }
  }, [])

  const fetchProfile = async (userId: string) => {
    if (!supabase) return
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error)
      } else if (data) {
        setProfile(data)
      } else {
        // Create profile if doesn't exist
        await createProfile(userId)
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const createProfile = async (userId: string) => {
    if (!supabase) return
    
    const username = `user_${userId.slice(0, 8)}`
    const { data, error } = await supabase
      .from('profiles')
      .insert({ id: userId, username, display_name: username })
      .select()
      .single()

    if (!error && data) {
      setProfile(data)
    }
  }

  const signUp = async (email: string, password: string, username: string) => {
    if (!supabase) {
      // Demo mode - simulate success
      setUser(DEMO_USER)
      setProfile({ ...DEMO_PROFILE, username, display_name: username })
      setSession(DEMO_SESSION)
      return { error: null }
    }
    
    const { data, error } = await supabase.auth.signUp({ email, password })
    
    if (error) return { error }
    
    if (data.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ username, display_name: username })
        .eq('id', data.user.id)
      
      if (profileError) return { error: profileError }
    }
    
    return { error: null }
  }

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      // Demo mode - simulate success
      setUser(DEMO_USER)
      setProfile(DEMO_PROFILE)
      setSession(DEMO_SESSION)
      return { error: null }
    }
    
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  const signOut = async () => {
    if (!supabase) {
      // Demo mode - clear demo user
      setUser(null)
      setProfile(null)
      setSession(null)
      return
    }
    await supabase.auth.signOut()
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!supabase || !user) return { error: new Error('Not authenticated') }
    
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
    
    if (!error) {
      await fetchProfile(user.id)
    }
    
    return { error }
  }

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id)
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      updateProfile,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}